import {
  Activity,
  AlertTriangle,
  Anchor,
  AppWindow,
  BarChart2,
  BarChart3,
  Bell,
  BookOpen,
  BookOpen as Book,
  Box,
  Brain,
  Bug,
  Building,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  Cloud,
  Code,
  Code2,
  Compass,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  File,
  FileCode2,
  FileText,
  FileText as FileTextIcon,
  Folder,
  FolderOpen,
  FolderOpen as FolderOpenIcon,
  GitBranch,
  GitMerge,
  Globe,
  Grid3x3,
  HardDrive,
  HardDrive as HardDriveIcon,
  Headphones,
  Heart,
  HelpCircle,
  Hexagon,
  Home,
  Layers,
  Layout,
  LayoutGrid,
  Library,
  ListTodo,
  Lock,
  MapPin,
  Monitor,
  Network,
  Package,
  Palette,
  Plug,
  PlusCircle,
  Puzzle,
  Search,
  Server,
  Settings,
  Shapes,
  Share2,
  Shield,
  ShoppingCart,
  Shuffle,
  Smartphone,
  Sparkles,
  Star,
  Terminal,
  TrendingUp,
  Trophy,
  Truck,
  User,
  UserCheck,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

export {
  Activity,
  AlertTriangle,
  Anchor,
  AppWindow,
  BarChart2,
  BarChart3,
  Bell,
  Book,
  BookOpen,
  Box,
  Brain,
  Bug,
  Building,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  Cloud,
  Code,
  Code2,
  Compass,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  File,
  FileCode2,
  FileText,
  FileTextIcon,
  Folder,
  FolderOpen,
  FolderOpenIcon,
  GitBranch,
  GitMerge,
  Globe,
  Grid3x3,
  HardDrive,
  HardDriveIcon,
  Headphones,
  Heart,
  HelpCircle,
  Hexagon,
  Home,
  Layers,
  Layout,
  LayoutGrid,
  Library,
  ListTodo,
  Lock,
  MapPin,
  Monitor,
  Network,
  Package,
  Palette,
  Plug,
  PlusCircle,
  Puzzle,
  Search,
  Server,
  Settings,
  Shapes,
  Share2,
  Shield,
  ShoppingCart,
  Shuffle,
  Smartphone,
  Sparkles,
  Star,
  Terminal,
  TrendingUp,
  Trophy,
  Truck,
  User,
  UserCheck,
  Users,
  Wrench,
  Zap,
};

import { IconComponent } from '@backstage/core-plugin-api';
import type { SvgIconProps } from "@material-ui/core";
import type { ComponentType } from "react";
import { createIconComponent } from './iconWrapper';

type IconType = IconComponent | ComponentType<SvgIconProps>;

// Wrap all Lucide icons to make them Backstage-compatible
export const AppIcons = {
  // Navigation
  home: createIconComponent(Home),
  search: createIconComponent(Search),
  settings: createIconComponent(Settings),
  create: createIconComponent(PlusCircle),

  api: createIconComponent(Code2),
  catalog: createIconComponent(FolderOpen),
  docs: createIconComponent(FileText),
  scaffolder: createIconComponent(PlusCircle),
  techdocs: createIconComponent(BookOpen),

  products: createIconComponent(AppWindow),
  cloud: createIconComponent(Cloud),

  library: createIconComponent(Library),
  resource: createIconComponent(HardDrive),
  pattern: createIconComponent(LayoutGrid),

  chevronDown: createIconComponent(ChevronDown),

  file: createIconComponent(File),
  folder: createIconComponent(Folder),
  folderOpen: createIconComponent(FolderOpenIcon),
  layers: createIconComponent(Layers),
  globe: createIconComponent(Globe),
  cpu: createIconComponent(Cpu),
  gitBranch: createIconComponent(GitBranch),
  mapPin: createIconComponent(MapPin),
  fileText: createIconComponent(FileTextIcon),
  wrench: createIconComponent(Wrench),
  hardDrive: createIconComponent(HardDriveIcon),
  grid: createIconComponent(Grid3x3),
  shapes: createIconComponent(Shapes),
  box: createIconComponent(Box),
  users: createIconComponent(Users),
  user: createIconComponent(User),
  database: createIconComponent(Database),
  smartphone: createIconComponent(Smartphone),
  fileCode: createIconComponent(FileCode2),
  share2: createIconComponent(Share2),
  zap: createIconComponent(Zap),
  shuffle: createIconComponent(Shuffle),
  puzzle: createIconComponent(Puzzle),
  code: createIconComponent(Code),
  code2: createIconComponent(Code2),
  layout: createIconComponent(Layout),
  package: createIconComponent(Package),
  server: createIconComponent(Server),
  plug: createIconComponent(Plug),
  brain: createIconComponent(Brain),
  monitor: createIconComponent(Monitor),
  barChart: createIconComponent(BarChart2),
  barChart2: createIconComponent(BarChart2),
  barChart3: createIconComponent(BarChart3),
  heart: createIconComponent(Heart),
  sparkles: createIconComponent(Sparkles),
  shield: createIconComponent(Shield),
  activity: createIconComponent(Activity),
  bell: createIconComponent(Bell),
  gitMerge: createIconComponent(GitMerge),
  terminal: createIconComponent(Terminal),
  star: createIconComponent(Star),
  userCheck: createIconComponent(UserCheck),
  shoppingCart: createIconComponent(ShoppingCart),
  truck: createIconComponent(Truck),
  creditCard: createIconComponent(CreditCard),
  dollarSign: createIconComponent(DollarSign),
  lock: createIconComponent(Lock),
  hexagon: createIconComponent(Hexagon),
  anchor: createIconComponent(Anchor),
  compass: createIconComponent(Compass),
  book: createIconComponent(Book),
  bookOpen: createIconComponent(BookOpen),
  building: createIconComponent(Building),
  building2: createIconComponent(Building2),
  alertTriangle: createIconComponent(AlertTriangle),
  trendingUp: createIconComponent(TrendingUp),
  palette: createIconComponent(Palette),
  helpCircle: createIconComponent(HelpCircle),
  bug: createIconComponent(Bug),
  listTodo: createIconComponent(ListTodo),
  checkCircle: createIconComponent(CheckCircle),
  calendar: createIconComponent(Calendar),
  trophy: createIconComponent(Trophy),
  headphones: createIconComponent(Headphones),
  network: createIconComponent(Network),
} as const;

// Entity kind to icon mappings
const ENTITY_KIND_ICONS: Record<string, keyof typeof AppIcons> = {
  Component: "package",
  API: "api",
  System: "layers",
  Domain: "globe",
  Resource: "server",
  Group: "users",
  User: "user",
  Template: "fileText",
  Location: "mapPin",
};

// Entity type to icon mappings
const ENTITY_TYPE_ICONS: Record<string, keyof typeof AppIcons> = {
  service: "cpu",
  website: "globe",
  library: "book",
  tool: "wrench",
  database: "database",
  resource: "box",
  pattern: "shapes",
  "data-pipeline": "gitBranch",
  "mobile-app": "smartphone",
  documentation: "fileText",
  docs: "bookOpen",
  api: "plug",
  graphql: "share2",
  grpc: "zap",
  storage: "hardDrive",
  cluster: "grid",
  "ml-model": "brain",
  dataset: "database",
  etl: "shuffle",
  plugin: "puzzle",
  sdk: "code",
  template: "layout",
  scaffold: "layers",
};

/**
 * Get icon component for a catalog entity
 */
export function getIconForEntity(kind: string, type?: string): string {
  // Prefer type-specific icon if available
  if (type && type in ENTITY_TYPE_ICONS) {
    return ENTITY_TYPE_ICONS[type];
  }

  // Fall back to kind icon
  if (kind in ENTITY_KIND_ICONS) {
    return ENTITY_KIND_ICONS[kind];
  }

  // Default fallback
  return "file";
}

/**
 * Resolve an icon by name
 */
export function resolveIcon(icon?: string | IconComponent): IconType | undefined {
  if (!icon) return undefined;

  // If already an IconComponent, return as-is
  if (typeof icon !== 'string') {
    return icon;
  }

  // Otherwise resolve string name to component
  return getIcon(icon);
}

/**
 * Get icon component by name (supports kebab-case and camelCase)
 */
export function getIcon(name: string): IconType | undefined {
  if (name in AppIcons) {
    return AppIcons[name as keyof typeof AppIcons];
  }

  const iconNameMap: Record<string, keyof typeof AppIcons> = {
    "file-text": "fileText",
    "hard-drive": "hardDrive",
    "map-pin": "mapPin",
    "git-branch": "gitBranch",
    "grid-3x3": "grid",
    "bar-chart": "barChart",
    "bar-chart-2": "barChart2",
    "bar-chart-3": "barChart3",
    "file-code": "fileCode",
    "file-code-2": "fileCode",
    "share-2": "share2",
    "git-merge": "gitMerge",
    "user-check": "userCheck",
    "shopping-cart": "shoppingCart",
    "credit-card": "creditCard",
    "dollar-sign": "dollarSign",
    "book-open": "bookOpen",
    "alert-triangle": "alertTriangle",
    "trending-up": "trendingUp",
    "help-circle": "helpCircle",
    "list-todo": "listTodo",
    "check-circle": "checkCircle",
    "code-2": "code2",
    "ml-model": "brain",
    "data-pipeline": "gitBranch",
    etl: "shuffle",
    sdk: "code",
    "plus-circle": "create",
  };

  const mappedName = iconNameMap[name.toLowerCase()];
  if (mappedName && mappedName in AppIcons) {
    return AppIcons[mappedName];
  }
  return undefined;
}
